import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

// Components
import GlobalStyles from '../../../../components/component-items/styles'
import { StatusBoxStyled } from '../../../../components/component-items/status-box'
import { TableCell } from '../../../../components/component-items/datatable'
import { Form, Col, Row } from 'react-bootstrap'
import { useFetch } from '../../../../components/component-items/helpers'

import { HandlingUnitCardProps } from './types'

export const HandlingUnitCard = ({
  sIndex,
  handling_unit_number,
  handlingUnits,
  setHandlingUnits,
  total,
  no_packaging,
  order_items,
  packaging_items,
  weight,
  length,
  width,
  height,
  handling_unit_type,
}: HandlingUnitCardProps) => {
  const [unit, setUnit] = useState('lb')
  const [validated, setValidated] = useState(false)
  const handlingUnitTypesRes = useFetch('/api/shipment/handling-unit-types', {})
  const handlingUnitTypes =
    handlingUnitTypesRes.loaded && Array.isArray(handlingUnitTypesRes.response) ? handlingUnitTypesRes.response : []

  function updateHandlingUnit(dimension: any, value: any) {
    const newHandlingUnits = [...handlingUnits]
    newHandlingUnits[sIndex] = { ...newHandlingUnits[sIndex], [dimension]: value }
    setHandlingUnits(newHandlingUnits)
    setValidated(true)
  }

  const isValid = () => {
    return weight > 0 && length > 0 && width > 0 && height > 0 && handling_unit_type
  }

  return (
    <GlobalStyles.FullPageCard>
      <GlobalStyles.CardHeader>
        <GlobalStyles.CardTitle>
          <h3>
            Handling Unit #{handling_unit_number}
            <small>{total} Total</small>
          </h3>
        </GlobalStyles.CardTitle>
      </GlobalStyles.CardHeader>
      <Form noValidate validated={validated} style={{ minHeight: 200, padding: '1em' }}>
        {/* First Row: Unit Type, Weight, and Unit - Full Width */}
        <Row style={{ padding: '.5em 1em 0' }}>
          <Form.Group as={Col} md="6" controlId="handling_unit_type" className="required">
            <Form.Label>
              Unit Type
              <span style={{ color: 'red', fontWeight: 'normal' }}>
                {!handling_unit_type && validated ? '(Required)' : ''}
              </span>
            </Form.Label>
            <Form.Select
              value={handling_unit_type?.id || ''}
              onChange={(e) =>
                updateHandlingUnit(
                  'handling_unit_type',
                  e.target.value
                    ? {
                        id: parseInt(e.target.value),
                        name: handlingUnitTypes.find((t) => t.id === parseInt(e.target.value))?.name || '',
                      }
                    : null
                )
              }
              required
              isInvalid={validated && !handling_unit_type}
            >
              <option value="">Select Type</option>
              {handlingUnitTypes.map((type: any) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">Please select a unit type</Form.Control.Feedback>
          </Form.Group>
          <Form.Group as={Col} md="4" controlId="scan" className="required">
            <Form.Label>Weight</Form.Label>
            <Form.Control
              type="number"
              placeholder="Weight"
              name="scan"
              value={unit === 'oz' ? weight : Math.round((weight / 16) * 100) / 100}
              onChange={(e) =>
                updateHandlingUnit('weight', unit === 'lb' ? Number(e.target.value) * 16 : e.target.value)
              }
              required
              isInvalid={validated && !weight}
            />
            <Form.Control.Feedback type="invalid">Please provide a valid weight</Form.Control.Feedback>
          </Form.Group>
          <Form.Group as={Col} md="2">
            <Form.Label>Unit</Form.Label>
            <Form.Select value={unit} onChange={(e) => setUnit(e.target.value)} required>
              <option value={'oz'}>OZ</option>
              <option value={'lb'}>LB</option>
            </Form.Select>
          </Form.Group>
        </Row>

        {/* Second Row: Dimensions */}
        <Row className="mt-3" style={{ padding: '0 1em' }}>
          <Form.Group as={Col} md="4" controlId="length" className="required">
            <Form.Label>Length</Form.Label>
            <Form.Control
              type="number"
              placeholder="Length"
              value={length}
              onChange={(e) => updateHandlingUnit('length', e.target.value)}
              required
              isInvalid={validated && !length}
            />
            <Form.Control.Feedback type="invalid">Please provide a valid length</Form.Control.Feedback>
          </Form.Group>
          <Form.Group as={Col} md="4" controlId="width" className="required">
            <Form.Label>Width</Form.Label>
            <Form.Control
              type="number"
              placeholder="Width"
              value={width}
              onChange={(e) => updateHandlingUnit('width', e.target.value)}
              required
              isInvalid={validated && !width}
            />
            <Form.Control.Feedback type="invalid">Please provide a valid width</Form.Control.Feedback>
          </Form.Group>
          <Form.Group as={Col} md="4" controlId="height" className="required">
            <Form.Label>Height</Form.Label>
            <Form.Control
              type="number"
              placeholder="Height"
              value={height}
              onChange={(e) => updateHandlingUnit('height', e.target.value)}
              required
              isInvalid={validated && !height}
            />
            <Form.Control.Feedback type="invalid">Please provide a valid height</Form.Control.Feedback>
          </Form.Group>
        </Row>

        {/* Third Row: Tables */}
        <Row className="mt-3">
          <Form.Group as={Col} md="7">
            <GlobalStyles.DataTable>
              <thead>
                <tr>
                  <th>
                    <span className="text"></span>
                  </th>
                  <th>
                    <span className="text">SKU &amp; Description</span>
                  </th>
                  <th>
                    <span className="text center">Ordered</span>
                  </th>
                  <th>
                    <span className="text center">Packed</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {order_items.map(({ img, p_id, sku, description, ordered, packed }) => (
                  <GlobalStyles.TableRow key={p_id}>
                    <TableCell center>
                      <img src={img} style={{ height: 'auto', maxWidth: 100, maxHeight: 50 }} />
                    </TableCell>
                    <TableCell input={<Link to={`/product/variant/${p_id}/`}>{sku}</Link>}>
                      <div>{description}</div>
                    </TableCell>
                    <TableCell
                      center
                      input={
                        <StatusBoxStyled style={{ width: 75, margin: 'auto' }} className={'silver'}>
                          {ordered}
                        </StatusBoxStyled>
                      }
                    />
                    <TableCell
                      center
                      input={
                        <StatusBoxStyled style={{ width: 75, margin: 'auto' }} className={'silver'}>
                          {packed}
                        </StatusBoxStyled>
                      }
                    />
                  </GlobalStyles.TableRow>
                ))}
              </tbody>
            </GlobalStyles.DataTable>
          </Form.Group>
          <Form.Group as={Col} md="5">
            <GlobalStyles.DataTable>
              <thead>
                <tr>
                  <th>
                    <span className="text"></span>
                  </th>
                  <th>
                    <span className="text">Packaging</span>
                  </th>
                  <th>
                    <span className="text center">Packed</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {packaging_items.map(({ img, p_id, sku, description, packed }) =>
                  packed > 0 ? (
                    <GlobalStyles.TableRow key={p_id}>
                      <TableCell center>
                        <img src={img} style={{ height: 'auto', maxWidth: 100, maxHeight: 50 }} />
                      </TableCell>
                      <TableCell input={<Link to={`/product/variant/${p_id}/`}>{sku}</Link>}>
                        <div>{description}</div>
                      </TableCell>
                      <TableCell
                        center
                        input={
                          <StatusBoxStyled style={{ width: 75, margin: 'auto' }} className={'silver'}>
                            {packed}
                          </StatusBoxStyled>
                        }
                      />
                    </GlobalStyles.TableRow>
                  ) : null
                )}
                {no_packaging && !packaging_items.length ? (
                  <GlobalStyles.TableRow>
                    <TableCell />
                    <TableCell input={'No Packaging'} />
                    <TableCell />
                  </GlobalStyles.TableRow>
                ) : null}
              </tbody>
            </GlobalStyles.DataTable>
          </Form.Group>
        </Row>
      </Form>
    </GlobalStyles.FullPageCard>
  )
}
